import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock, Loader2, LogOut, Trash2, Plus, Pencil, X, Mail, Star, Check, Upload, Download, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminLogin, isLoggedIn, logout, getStats,
  getApplications, updateApplication, deleteApplication,
  getMessages, deleteMessage,
  getNews, createNews, updateNews, deleteNews,
  getMembers, createMember, updateMember, deleteMember,
  uploadMemberPhoto, importMembers, downloadCsv,
} from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Subscribers } from '../components/admin/SubscribersAdmin';
import { EventsManager, RsvpsManager } from '../components/admin/EventsAdmin';

function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      sessionStorage.setItem('ah_admin_token', token);
      onLogin();
    } catch {
      toast.error('Incorrect password');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1f2c33] px-6">
      <form onSubmit={submit} className="bg-white rounded-sm p-10 w-full max-w-[420px] shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-[#eef4f8] flex items-center justify-center mx-auto mb-6">
          <Lock size={28} className="text-[#4a9cd4]" />
        </div>
        <h1 className="font-serif-ah text-[30px] text-center text-[#2f2f2f] mb-2">Admin Login</h1>
        <p className="font-sans-ah text-[14px] text-center text-[#888] font-300 mb-7">Almaguin Highlands Chamber</p>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter admin password"
          className="w-full px-4 py-3.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] mb-4"
        />
        <button type="submit" disabled={loading} className="btn-ah-solid w-full font-sans-ah text-[13px] py-4 rounded-sm flex items-center justify-center gap-2">
          {loading && <Loader2 size={16} className="animate-spin" />} LOG IN
        </button>
      </form>
    </div>
  );
}

const EMPTY_NEWS = { title: '', tag: 'NEWS', excerpt: '', body: '', image: '', date: new Date().toISOString().slice(0, 10), published: true };

function NewsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => getNews(true).then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await updateNews(editing.id, editing);
      else await createNews(editing);
      toast.success('Saved');
      setEditing(null); load();
    } catch { toast.error('Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await deleteNews(id); toast.success('Deleted'); load();
  };

  const inputCls = 'w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4]';

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif-ah text-[26px] text-[#2f2f2f]">Manage News &amp; Events</h2>
        <button onClick={() => setEditing({ ...EMPTY_NEWS })} className="btn-ah-solid font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2">
          <Plus size={16} /> NEW POST
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[#f8f6f2] rounded-sm p-6 mb-8 border border-neutral-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif-ah text-[20px]">{editing.id ? 'Edit' : 'New'} Post</h3>
            <button type="button" onClick={() => setEditing(null)}><X size={20} className="text-[#888]" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input className={inputCls} placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <input className={inputCls} placeholder="Tag (EVENT / NEW MEMBER / NEWS)" value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} />
            <input className={inputCls} placeholder="Image URL" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} />
            <input type="date" className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
          </div>
          <textarea className={inputCls + ' mt-4'} rows={2} placeholder="Short excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
          <textarea className={inputCls + ' mt-4'} rows={5} placeholder="Full article body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
          <label className="flex items-center gap-2 mt-4 font-sans-ah text-[14px]">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published
          </label>
          <button type="submit" className="btn-ah-solid font-sans-ah text-[12px] px-6 py-3 rounded-sm mt-5">SAVE POST</button>
        </form>
      )}

      <div className="space-y-3">
        {items.map((n) => (
          <div key={n.id} className="flex items-center gap-4 bg-white border border-neutral-100 rounded-sm p-4">
            <div className="w-16 h-16 rounded-sm bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${n.image})` }} />
            <div className="flex-1 min-w-0">
              <p className="font-serif-ah text-[18px] text-[#2f2f2f] truncate">{n.title}</p>
              <p className="font-sans-ah text-[12px] text-[#999]">{n.tag} • {n.date} • {n.published ? 'Published' : 'Draft'}</p>
            </div>
            <button onClick={() => setEditing(n)} className="p-2 text-[#4a9cd4] hover:bg-[#eef4f8] rounded-sm"><Pencil size={17} /></button>
            <button onClick={() => remove(n.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const load = useCallback(() => getApplications().then(setApps).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const mark = async (id, status) => { await updateApplication(id, status); toast.success('Updated'); load(); };
  const remove = async (id) => { if (!window.confirm('Delete?')) return; await deleteApplication(id); load(); };

  return (
    <div>
      <h2 className="font-serif-ah text-[26px] text-[#2f2f2f] mb-6">Membership Applications</h2>
      {apps.length === 0 ? <p className="font-sans-ah text-[#888]">No applications yet.</p> : (
        <div className="space-y-4">
          {apps.map((a) => (
            <div key={a.id} className="bg-white border border-neutral-100 rounded-sm p-6">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <p className="font-serif-ah text-[20px] text-[#2f2f2f]">{a.business_name}</p>
                  <p className="font-sans-ah text-[13px] text-[#666]">{a.contact_name} • {a.email} • {a.phone}</p>
                  {a.membership_level && <p className="font-sans-ah text-[12px] text-[#4a9cd4] mt-1">{a.membership_level}</p>}
                </div>
                <span className={`font-sans-ah text-[11px] tracking-wide-ah px-3 py-1.5 rounded-sm ${a.status === 'New' ? 'bg-[#eef4f8] text-[#4a9cd4]' : 'bg-green-50 text-green-600'}`}>{a.status.toUpperCase()}</span>
              </div>
              {a.message && <p className="font-sans-ah text-[14px] text-[#555] font-300 mt-3 pt-3 border-t border-neutral-100">{a.message}</p>}
              <div className="flex gap-2 mt-4">
                {a.status === 'New' && <button onClick={() => mark(a.id, 'Reviewed')} className="font-sans-ah text-[12px] flex items-center gap-1.5 text-green-600 border border-green-200 px-4 py-2 rounded-sm hover:bg-green-50"><Check size={14} /> MARK REVIEWED</button>}
                <button onClick={() => remove(a.id)} className="font-sans-ah text-[12px] flex items-center gap-1.5 text-red-500 border border-red-200 px-4 py-2 rounded-sm hover:bg-red-50"><Trash2 size={14} /> DELETE</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState([]);
  const load = useCallback(() => getMessages().then(setMsgs).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm('Delete?')) return; await deleteMessage(id); load(); };
  return (
    <div>
      <h2 className="font-serif-ah text-[26px] text-[#2f2f2f] mb-6">Contact Messages</h2>
      {msgs.length === 0 ? <p className="font-sans-ah text-[#888]">No messages yet.</p> : (
        <div className="space-y-4">
          {msgs.map((m) => (
            <div key={m.id} className="bg-white border border-neutral-100 rounded-sm p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-serif-ah text-[19px] text-[#2f2f2f]">{m.subject || 'General Enquiry'}</p>
                  <p className="font-sans-ah text-[13px] text-[#666] flex items-center gap-2 mt-1"><Mail size={14} className="text-[#4a9cd4]" /> {m.name} • {m.email} • {m.phone}</p>
                </div>
                <button onClick={() => remove(m.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={17} /></button>
              </div>
              <p className="font-sans-ah text-[14px] text-[#555] font-300 mt-3 pt-3 border-t border-neutral-100 whitespace-pre-line">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_MEMBER = {
  name: '', contact_name: '', email: '', phone: '', website: '',
  address: '', city: '', postal_code: '', categories: [], description: '',
  membership_level: '', status: 'Active', featured: false, image: '', gallery: [],
  service_area: '', business_hours: '', linkedin: '', facebook: '', instagram: '',
};
const MEMBER_STATUSES = ['Active', 'Pending', 'Inactive'];
const MEMBER_LEVELS = [
  'Business Member - 1 to 3 Employees',
  'Business Member - 4 to 9 Employees',
  'Business Member - 10 to 20 Employees',
  'Business Member - 21 and over Employees',
  'Non-profit and Community Member',
];

function MembersManager() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // holds member object or new
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const importRef = useRef(null);
  const photoRef = useRef(null);
  const galleryRef = useRef(null);
  const load = useCallback(() => getMembers({ status: 'all' }).then(setMembers).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const toggleFeatured = async (m) => { await updateMember(m.id, { ...m, featured: !m.featured }); toast.success('Updated'); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this member?')) return; await deleteMember(id); toast.success('Deleted'); load(); };
  const filtered = members.filter((m) => m.name.toLowerCase().includes(q.toLowerCase()));

  const startAdd = () => setEditing({ ...EMPTY_MEMBER });
  const startEdit = (m) => setEditing({ ...EMPTY_MEMBER, ...m, categories: m.categories || [], gallery: m.gallery || [] });
  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const doImport = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setImporting(true);
    try {
      const res = await importMembers(f);
      toast.success(`Imported: ${res.created} added, ${res.updated} updated, ${res.skipped} skipped`);
      load();
    } catch {
      toast.error('Import failed. Use an .xls, .xlsx or .csv file.');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const doUploadPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadMemberPhoto(f);
      setField('image', res.absoluteUrl);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const doUploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const res = await uploadMemberPhoto(f);
        urls.push(res.absoluteUrl);
      }
      setEditing((ed) => ({ ...ed, gallery: [...(ed.gallery || []), ...urls] }));
      toast.success(`${urls.length} photo${urls.length > 1 ? 's' : ''} added to gallery`);
    } catch {
      toast.error('Gallery upload failed.');
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (url) => setEditing((ed) => ({ ...ed, gallery: (ed.gallery || []).filter((g) => g !== url) }));

  const save = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) { toast.error('Business name is required.'); return; }
    const payload = {
      ...editing,
      categories: Array.isArray(editing.categories)
        ? editing.categories
        : String(editing.categories).split(',').map((c) => c.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (editing.id) await updateMember(editing.id, payload);
      else await createMember(payload);
      toast.success(editing.id ? 'Member updated' : 'Member added');
      setEditing(null); load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4]';
  const catString = Array.isArray(editing?.categories) ? editing.categories.join(', ') : (editing?.categories || '');

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="font-serif-ah text-[26px] text-[#2f2f2f]">Members ({members.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members..." className="px-4 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4] w-[180px]" />
          <input ref={importRef} type="file" accept=".xls,.xlsx,.csv" onChange={doImport} className="hidden" data-testid="import-members-input" />
          <button onClick={() => importRef.current && importRef.current.click()} disabled={importing} data-testid="import-members-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
            {importing ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} IMPORT
          </button>
          <button onClick={() => downloadCsv('members')} data-testid="export-members-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 whitespace-nowrap">
            <Download size={15} /> EXPORT CSV
          </button>
          <button onClick={startAdd} data-testid="add-member-btn" className="btn-ah-solid font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2 whitespace-nowrap">
            <Plus size={16} /> ADD MEMBER
          </button>
        </div>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="member-form" className="bg-[#f8f6f2] rounded-sm p-6 mb-8 border border-neutral-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif-ah text-[20px]">{editing.id ? 'Edit Member' : 'Add New Member'}</h3>
            <button type="button" onClick={() => setEditing(null)} aria-label="Close form"><X size={20} className="text-[#888]" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="mm-name" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Business Name *</label>
              <input id="mm-name" data-testid="member-name-input" className={inputCls} value={editing.name} onChange={(e) => setField('name', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-contact" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Contact Name</label>
              <input id="mm-contact" data-testid="member-contact-input" className={inputCls} value={editing.contact_name} onChange={(e) => setField('contact_name', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-email" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Email</label>
              <input id="mm-email" data-testid="member-email-input" className={inputCls} value={editing.email} onChange={(e) => setField('email', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-phone" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Phone</label>
              <input id="mm-phone" data-testid="member-phone-input" className={inputCls} value={editing.phone} onChange={(e) => setField('phone', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-website" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Website</label>
              <input id="mm-website" data-testid="member-website-input" className={inputCls} value={editing.website} onChange={(e) => setField('website', e.target.value)} />
            </div>
            <div>
              <label className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Photo</label>
              <div className="flex items-center gap-3">
                {editing.image ? (
                  <img src={editing.image} alt="member" className="w-11 h-11 rounded-sm object-cover border border-neutral-200 shrink-0" />
                ) : (
                  <div className="w-11 h-11 rounded-sm bg-neutral-100 border border-neutral-200 shrink-0" />
                )}
                <input ref={photoRef} type="file" accept="image/*" onChange={doUploadPhoto} className="hidden" data-testid="member-photo-input" />
                <button type="button" onClick={() => photoRef.current && photoRef.current.click()} disabled={uploading} data-testid="member-photo-upload-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 disabled:opacity-60">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {editing.image ? 'REPLACE' : 'UPLOAD'}
                </button>
                {editing.image && (
                  <button type="button" onClick={() => setField('image', '')} className="font-sans-ah text-[12px] text-red-500 hover:underline">Remove</button>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="mm-address" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Address</label>
              <input id="mm-address" data-testid="member-address-input" className={inputCls} value={editing.address} onChange={(e) => setField('address', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-city" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">City / Town</label>
              <input id="mm-city" data-testid="member-city-input" className={inputCls} value={editing.city} onChange={(e) => setField('city', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-postal" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Postal Code</label>
              <input id="mm-postal" data-testid="member-postal-input" className={inputCls} value={editing.postal_code} onChange={(e) => setField('postal_code', e.target.value)} />
            </div>
            <div>
              <label htmlFor="mm-status" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Status</label>
              <select id="mm-status" data-testid="member-status-select" className={inputCls} value={editing.status} onChange={(e) => setField('status', e.target.value)}>
                {MEMBER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="mm-level" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Membership Level</label>
              <select id="mm-level" data-testid="member-level-select" className={inputCls} value={editing.membership_level} onChange={(e) => setField('membership_level', e.target.value)}>
                <option value="">— Select —</option>
                {MEMBER_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="mm-categories" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Categories (comma-separated)</label>
              <input id="mm-categories" data-testid="member-categories-input" className={inputCls} value={catString} onChange={(e) => setField('categories', e.target.value)} placeholder="e.g. Retail, Food and Beverage" />
            </div>
            <div>
              <label htmlFor="mm-service-area" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Service Area</label>
              <input id="mm-service-area" data-testid="member-service-area-input" className={inputCls} value={editing.service_area} onChange={(e) => setField('service_area', e.target.value)} placeholder="e.g. Almaguin Highlands & Muskoka" />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="mm-desc" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Description</label>
            <textarea id="mm-desc" data-testid="member-description-input" rows={3} className={inputCls} value={editing.description} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <div className="mt-4">
            <label htmlFor="mm-hours" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Business Hours</label>
            <textarea id="mm-hours" data-testid="member-hours-input" rows={3} className={inputCls} value={editing.business_hours} onChange={(e) => setField('business_hours', e.target.value)} placeholder={'e.g.\nMon–Fri: 9am–5pm\nSat: 10am–2pm\nSun: Closed'} />
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div>
              <label htmlFor="mm-linkedin" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">LinkedIn</label>
              <input id="mm-linkedin" data-testid="member-linkedin-input" className={inputCls} value={editing.linkedin} onChange={(e) => setField('linkedin', e.target.value)} placeholder="https://linkedin.com/company/..." />
            </div>
            <div>
              <label htmlFor="mm-facebook" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Facebook</label>
              <input id="mm-facebook" data-testid="member-facebook-input" className={inputCls} value={editing.facebook} onChange={(e) => setField('facebook', e.target.value)} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label htmlFor="mm-instagram" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Instagram</label>
              <input id="mm-instagram" data-testid="member-instagram-input" className={inputCls} value={editing.instagram} onChange={(e) => setField('instagram', e.target.value)} placeholder="https://instagram.com/..." />
            </div>
          </div>
          <label className="flex items-center gap-2 mt-4 font-sans-ah text-[14px]">
            <input type="checkbox" data-testid="member-featured-checkbox" checked={editing.featured} onChange={(e) => setField('featured', e.target.checked)} /> Feature on homepage
          </label>

          <div className="mt-5 pt-5 border-t border-neutral-200">
            <div className="flex items-center justify-between mb-3">
              <label className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Photo Gallery ({(editing.gallery || []).length})</label>
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={doUploadGallery} className="hidden" data-testid="member-gallery-input" />
              <button type="button" onClick={() => galleryRef.current && galleryRef.current.click()} disabled={galleryUploading} data-testid="member-gallery-upload-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 disabled:opacity-60">
                {galleryUploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} ADD PHOTOS
              </button>
            </div>
            {(editing.gallery || []).length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {editing.gallery.map((url) => (
                  <div key={url} className="relative group aspect-square">
                    <img src={url} alt="gallery" className="w-full h-full object-cover rounded-sm border border-neutral-200" />
                    <button type="button" onClick={() => removeGalleryImage(url)} aria-label="Remove photo" className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-sans-ah text-[13px] text-[#999] font-300">No gallery photos yet. Add a few to show on this member's profile page.</p>
            )}
          </div>
          <div className="flex gap-3 mt-5">
            <button type="submit" data-testid="member-save-btn" disabled={saving} className="btn-ah-solid font-sans-ah text-[12px] px-6 py-3 rounded-sm flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />} {editing.id ? 'SAVE CHANGES' : 'ADD MEMBER'}
            </button>
            <button type="button" onClick={() => setEditing(null)} className="font-sans-ah text-[12px] tracking-wide-ah px-6 py-3 rounded-sm border border-neutral-300 text-[#666] hover:bg-white">CANCEL</button>
          </div>
        </form>
      )}

      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
        {filtered.map((m) => (
          <div key={m.id} className="flex items-center gap-4 bg-white border border-neutral-100 rounded-sm p-3.5">
            <div className="flex-1 min-w-0">
              <p className="font-sans-ah text-[15px] text-[#2f2f2f] font-500 truncate">{m.name}</p>
              <p className="font-sans-ah text-[12px] text-[#999] truncate">{m.city || '—'} • {m.status} • {(m.categories || [])[0] || '—'}</p>
            </div>
            <button onClick={() => toggleFeatured(m)} title="Toggle featured" className={`p-2 rounded-sm ${m.featured ? 'text-amber-500 bg-amber-50' : 'text-[#bbb] hover:bg-neutral-50'}`}>
              <Star size={17} fill={m.featured ? 'currentColor' : 'none'} />
            </button>
            <button onClick={() => startEdit(m)} data-testid={`edit-member-${m.id}`} title="Edit member" className="p-2 text-[#4a9cd4] hover:bg-[#eef4f8] rounded-sm"><Pencil size={17} /></button>
            <button onClick={() => remove(m.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(isLoggedIn());
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (authed) getStats().then(setStats).catch(() => { logout(); setAuthed(false); });
  }, [authed]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#f8f6f2] pt-24 pb-16">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-serif-ah text-[36px] text-[#2f2f2f]">Admin Dashboard</h1>
            <p className="font-sans-ah text-[14px] text-[#888]">Almaguin Highlands Chamber of Commerce</p>
          </div>
          <button onClick={() => { logout(); setAuthed(false); }} className="font-sans-ah text-[12px] tracking-wide-ah flex items-center gap-2 text-[#666] border border-neutral-300 px-5 py-3 rounded-sm hover:bg-white">
            <LogOut size={15} /> LOG OUT
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              ['Members', stats.members], ['Active', stats.active_members],
              ['New Applications', stats.new_applications], ['Subscribers', stats.subscribers],
            ].map(([label, val]) => (
              <div key={label} className="bg-white rounded-sm p-6 border border-neutral-100">
                <p className="font-serif-ah text-[38px] text-[#4a9cd4] leading-none">{val}</p>
                <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#888] uppercase mt-2">{label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="applications" className="w-full">
          <TabsList className="bg-white border border-neutral-200 rounded-sm p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="applications" className="font-sans-ah text-[13px]">Applications</TabsTrigger>
            <TabsTrigger value="messages" className="font-sans-ah text-[13px]">Messages</TabsTrigger>
            <TabsTrigger value="news" className="font-sans-ah text-[13px]">News &amp; Events</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="font-sans-ah text-[13px]">Events</TabsTrigger>
            <TabsTrigger value="rsvps" data-testid="admin-tab-rsvps" className="font-sans-ah text-[13px]">RSVPs</TabsTrigger>
            <TabsTrigger value="members" className="font-sans-ah text-[13px]">Members</TabsTrigger>
            <TabsTrigger value="subscribers" className="font-sans-ah text-[13px]">Subscribers</TabsTrigger>
          </TabsList>
          <TabsContent value="applications"><Applications /></TabsContent>
          <TabsContent value="messages"><Messages /></TabsContent>
          <TabsContent value="news"><NewsManager /></TabsContent>
          <TabsContent value="events"><EventsManager /></TabsContent>
          <TabsContent value="rsvps"><RsvpsManager /></TabsContent>
          <TabsContent value="members"><MembersManager /></TabsContent>
          <TabsContent value="subscribers"><Subscribers /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
